import React, { FC } from 'react';
import { Tab, Tabs } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { brandclub_colors, hexToRgba } from '@stackline/ui';
import { buildLinkParams } from 'src/components/EntityPage/LeftNav/LeftNavConfig';
import { useAppSelector, useHistory, useLocation } from 'src/utils/Hooks';
import { ChildSubtab } from 'src/components/CommonV2Components/GenericSideBar/types';
import { CSSProperties } from '@mui/styles';
import _merge from 'lodash/merge';
import ReduxStore from 'src/types/store/reduxStore';
import { UpdateUrlQueryParams } from 'src/utils/app';
import { Location } from 'history';
import { useAtlasV2Routes } from 'src/components/AtlasV2Components/AtlasV2Sidebar/useAtlasV2Routes';
import { AppParamTabsProps } from 'src/components/BeaconV2Components/SubtabNav/SubtabNavigation';
import colors from 'src/utils/colors';

const useStyles = (tabsStyles: CSSProperties = {}) =>
  makeStyles({
    tabsDefaultStyles: _merge(
      {
        minHeight: 'unset',
        width: '100%',
        '& div.MuiTabs-scroller': {
          borderBottom: `1px solid ${hexToRgba(colors.darkBlue, 0.12)}`,
          '& div.MuiTabs-flexContainer': {
            gap: 32,
            minHeight: 'unset'
          },
          '& span.MuiTabs-indicator': {
            backgroundColor: colors.darkBlue,
            borderRadius: 3
          }
        }
      },
      tabsStyles
    ),
    tabDefaultStyles: {
      color: brandclub_colors.gray5,
      fontSize: '14px',
      fontWeight: 500,
      fontFamily: 'Roboto',
      letterSpacing: 0.09,
      minHeight: 'unset',
      minWidth: 'unset',
      justifyContent: 'flex-end',
      paddingBottom: 7.5,
      padding: 0,
      textTransform: 'none',
      cursor: 'pointer',
      '&.Mui-selected': {
        color: colors.darkBlue
      },
      '&.Mui-disabled': {
        color: hexToRgba(colors.darkBlue, 0.12)
      }
    }
  })();

export const buildAtlasV2TabAndSubtabLink = ({
  app,
  location,
  tab,
  subtab
}: {
  app: ReduxStore['app'];
  location: Location;
  tab: string;
  subtab: string;
  defaultQueryParams?: UpdateUrlQueryParams;
}) => {
  const { pathname } = location;
  const { linkParams } = buildLinkParams({ tab, subtab }, true);
  const { searchParams, compareParams, filterParams } = app.queryParams;
  const showCompareParams = linkParams.includes('ctab') ? '' : compareParams;
  const subtabLink = `${pathname}${searchParams}&${linkParams}${showCompareParams}${filterParams}`;

  return subtabLink;
};

const AppParamTabs: FC<AppParamTabsProps> = ({ tabs, tab, disableNextTabs, location, history }) => {
  const classes = useStyles();
  const app = useAppSelector((state) => state.app);

  return (
    <Tabs
      value={tab}
      variant="scrollable"
      scrollButtons={false}
      className={classes.tabsDefaultStyles}
      sx={{ paddingTop: tabs.length ? '16px' : '32px' }}
    >
      {tabs.map((t, index) => {
        return (
          <Tab
            key={index}
            value={t.value}
            label={t.label}
            disableRipple
            disabled={disableNextTabs && index > tabs.findIndex((tb) => tb.value === tab)}
            className={classes.tabDefaultStyles}
            onClick={() =>
              history.push(
                buildAtlasV2TabAndSubtabLink({
                  app,
                  location,
                  tab: t.parentTab,
                  subtab: t.value,
                  defaultQueryParams: t.defaultQueryParams
                })
              )
            }
          />
        );
      })}
    </Tabs>
  );
};

const AtlasV2SubtabNav = (props: { childSubtabs?: ChildSubtab[] } = {}) => {
  const history = useHistory();
  const location = useLocation();
  const { currentRoute, currentSubtab } = useAtlasV2Routes();
  const { childSubtabs } = currentRoute;

  return (
    <AppParamTabs location={location} history={history} tabs={props.childSubtabs || childSubtabs} tab={currentSubtab} />
  );
};

export default AtlasV2SubtabNav;
